import { IAPIRoutes } from "../../../../config";

export const InsightsRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}) => {
  const { GET, PUT } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  const getTOPAllowedMonths: IGetTOPAllowedMonthsAPI = async (payload) =>
    GET(
      APIRoutes.apiGetTOPAllowedMonths.replace(
        "{clinic_id}",
        payload.clinic_id?.toString()
      ),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getTOCAllowedMonths: IGetTOCAllowedMonthsAPI = async (payload) =>
    GET(
      APIRoutes.apiGetTOCAllowedMonths.replace(
        "{clinic_id}",
        payload.clinic_id?.toString()
      ),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getTOPAPI: IGetTOPAPI = async (payload) =>
    GET(
      APIRoutes.apiGetTOP.replace("{clinic_id}", payload.clinic_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getTOPPreventedSummaryAPI: IGetTOPPreventedSummaryAPI = async (
    payload
  ) =>
    GET(
      APIRoutes.apiGetTOPPreventedSummary.replace(
        "{clinic_id}",
        payload.clinic_id?.toString()
      ),
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getTOCAPI: IGetTOCAPI = async (payload) =>
    GET(
      APIRoutes.apiGetTOC.replace("{clinic_id}", payload.clinic_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getTOCRootCauseAPI: IGetTOCRootCauseAPI = async (payload) =>
    GET(
      APIRoutes.apiGetTOCRootCause
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{TO_EVENT_ID}", payload.TO_EVENT_ID),
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getEventNoteAPI: IGetEventNoteAPI = async (payload) =>
    GET(
      APIRoutes.apiGetTOCEventNote
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{TO_EVENT_ID}", payload.TO_EVENT_ID),
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const putEventNoteAPI: IPutEventNoteAPI = async (payload) =>
    PUT(
      APIRoutes.apiPutTOCEventNote
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{TO_EVENT_ID}", payload.TO_EVENT_ID),
      { note: payload.note },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getDischargeAllowedMonths: IGetDischargeAllowedMonthsAPI = async (
    payload
  ) =>
    GET(
      APIRoutes.apiGetDischargeAllowedMonths.replace(
        "{clinic_id}",
        payload.clinic_id?.toString()
      ),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getDischargeAPI: IGetDischargeAPI = async (payload) =>
    GET(
      APIRoutes.apiGetDischarge.replace(
        "{clinic_id}",
        payload.clinic_id?.toString()
      ),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getOutcomesFacilitiesAPI: IGetOutcomesFacilitiesAPI = async (
    payload
  ) => {
    // Clean payload - remove clinic_id from query params and only include defined clinic_ids
    const queryParams: any = {};
    if (payload.clinic_ids && payload.clinic_ids.length > 0) {
      queryParams.clinic_ids = payload.clinic_ids;
    }

    return GET(
      APIRoutes.apiGetOutcomesFacilities.replace(
        "{clinic_id}",
        payload.clinic_id?.toString()
      ),
      queryParams,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
  };

  const getOutcomesAvailablePeriodsAPI: IGetOutcomesAvailablePeriodsAPI =
    async (payload) => {
      const queryParams: Record<string, string> = {};
      if (payload.metric_type) {
        queryParams.metric_type = payload.metric_type;
      }
      if (payload.filter_clinic_id !== undefined) {
        queryParams.filter_clinic_id = payload.filter_clinic_id.toString();
      }

      return GET(
        APIRoutes.apiGetOutcomesAvailablePeriods.replace(
          "{clinic_id}",
          payload.clinic_id?.toString()
        ),
        queryParams,
        {
          Authorization: `Bearer ${await getAuthenticationToken()}`,
        }
      );
    };

  const getOutcomesTimeSeriesAPI: IGetOutcomesTimeSeriesAPI = async (
    payload
  ) => {
    // Clean payload - only include defined parameters
    const queryParams: any = {
      metric_type: payload.metric_type,
    };

    if (payload.normalization)
      queryParams.normalization = payload.normalization;
    if (payload.patient_type) queryParams.patient_type = payload.patient_type;
    if (payload.trend_type) queryParams.trend_type = payload.trend_type;
    if (payload.start_month) queryParams.start_month = payload.start_month;
    if (payload.end_month) queryParams.end_month = payload.end_month;
    if (payload.months_before !== undefined)
      queryParams.months_before = payload.months_before;
    if (payload.months_after !== undefined)
      queryParams.months_after = payload.months_after;
    if (payload.clinic_ids && payload.clinic_ids.length > 0)
      queryParams.clinic_ids = payload.clinic_ids;
    if (payload.regions && payload.regions.length > 0)
      queryParams.regions = payload.regions;
    if (payload.states && payload.states.length > 0)
      queryParams.states = payload.states;
    if (payload.exclude_clinic_ids && payload.exclude_clinic_ids.length > 0) {
      queryParams.exclude_clinic_ids = payload.exclude_clinic_ids;
    }

    return GET(
      APIRoutes.apiGetOutcomesTimeSeries.replace(
        "{clinic_id}",
        payload.clinic_id?.toString()
      ),
      queryParams,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
  };

  const getOutcomesFilterOptionsAPI: IGetOutcomesFilterOptionsAPI = async (
    payload
  ) =>
    GET(
      APIRoutes.apiGetOutcomesFilterOptions.replace(
        "{clinic_id}",
        payload.clinic_id?.toString()
      ),
      {}, // No query params needed
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getDashboardOverviewAPI: IGetDashboardOverviewAPI = async (payload) => {
    const queryParams: Record<string, string> = {};
    if (payload.start_dt) queryParams.start_dt = payload.start_dt;
    if (payload.end_dt) queryParams.end_dt = payload.end_dt;

    return GET(
      APIRoutes.apiGetDashboardOverview.replace(
        "{clinic_id}",
        payload.clinic_id?.toString()
      ),
      queryParams,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
  };

  const getMyNeoCostAPI: IGetMyNeoCostAPI = async (payload) => {
    const queryParams: Record<string, string> = {};
    if (payload.start_dt) queryParams.start_dt = payload.start_dt;
    if (payload.end_dt) queryParams.end_dt = payload.end_dt;

    return GET(APIRoutes.apiGetMyNeoCost, queryParams, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  return {
    getTOPAPI,
    getTOPPreventedSummaryAPI,
    getTOCAPI,
    getTOPAllowedMonths,
    getTOCAllowedMonths,
    getTOCRootCauseAPI,
    getDischargeAllowedMonths,
    getDischargeAPI,
    getOutcomesFacilitiesAPI,
    getOutcomesAvailablePeriodsAPI,
    getOutcomesTimeSeriesAPI,
    getOutcomesFilterOptionsAPI,
    getDashboardOverviewAPI,
    getMyNeoCostAPI,
    getEventNoteAPI,
    putEventNoteAPI,
  };
};
